/**
 * Building blocks shared by the expansion's demolition, sabotage and endurance missions (10 §2: 13-20, 33-36):
 * things put down at exact spots (a train on its siding, guns in their pits), the men who serve them, lamps,
 * and the columns and waves that are not on the map until a timed event puts them there.
 */

import { atan2 } from '../../sim/math';
import type { EnemyKind, PropDef, PropKind, SpawnDef } from '../../sim/types';
import { NO_BUILD, NO_STAND } from '../builder';
import { FLAG } from '../grid';
import type { Point } from '../types';
import type { GoalCtx } from './types';

export const half = (v: number): number => Math.round(v * 2) / 2;

export interface PutOptions {
  target?: boolean;
  yaw?: number;
  length?: number;
  margin?: number;
  /** Flags the footprint may not touch; default everything nothing solid may stand on. */
  avoid?: number;
}

/** What may stand on a road or a track bed: a parked vehicle, a wagon on its siding. */
export const ON_ROAD = NO_BUILD & ~FLAG.road;

/** A prop at an exact spot. The mission cannot exist without it: no room marks the attempt for a re-roll. */
export function put(g: GoalCtx, kind: PropKind, x: number, z: number, o: PutOptions = {}): PropDef {
  const shape = o.length !== undefined ? { length: o.length } : {};
  const yaw = o.yaw !== undefined ? { yaw: o.yaw } : {};
  const target = o.target === true ? { target: true } : {};
  const at = { margin: o.margin ?? 0.8, avoid: o.avoid ?? NO_BUILD, ...shape, ...yaw, ...target };
  const prop = g.map.place(kind, half(x), half(z), at);
  if (prop) return prop;
  g.map.problems.push(`${g.ctx.spec.id}: no room for the ${kind} at ${half(x)}, ${half(z)}`);
  return { id: -1, kind, x: half(x), z: half(z), yaw: o.yaw ?? 0 };
}

/**
 * Things standing nose to tail - coupled wagons, moored barges - are closer together than the scratch mask
 * (one metre cells, padded footprints) can tell apart. So the whole block is asked for once, and its members
 * are then put down without asking. Returns them all as flagged targets, or nothing (and a re-roll).
 */
export function putBlock(
  g: GoalCtx, items: ReadonlyArray<{ kind: PropKind; x: number; z: number }>, avoid: number = NO_BUILD, margin = 0.5,
): PropDef[] {
  if (items.length === 0) return [];
  const box = { x0: Infinity, z0: Infinity, x1: -Infinity, z1: -Infinity };
  for (const item of items) {
    const b = g.map.bounds(item.kind, half(item.x), half(item.z));
    box.x0 = Math.min(box.x0, b.x0);
    box.z0 = Math.min(box.z0, b.z0);
    box.x1 = Math.max(box.x1, b.x1);
    box.z1 = Math.max(box.z1, b.z1);
  }
  if (g.map.mask.hitsRect(box.x0 - margin, box.z0 - margin, box.x1 + margin, box.z1 + margin, avoid)) {
    g.map.problems.push(`${g.ctx.spec.id}: no room for the ${items[0]?.kind ?? 'block'}s in line`);
    return [];
  }
  return items.map((item) => g.map.add(item.kind, half(item.x), half(item.z), { target: true }));
}

/** As `put`, for dressing the mission can do without: whatever does not fit is left out. */
export function dress(g: GoalCtx, kind: PropKind, x: number, z: number, o: PutOptions = {}): PropDef | null {
  const shape = o.length !== undefined ? { length: o.length } : {};
  const yaw = o.yaw !== undefined ? { yaw: o.yaw } : {};
  return g.map.place(kind, half(x), half(z), { margin: o.margin ?? 0.6, avoid: o.avoid ?? NO_BUILD, ...shape, ...yaw });
}

/** The man who serves an emplacement - a gun in its pit, a casemate: he sits in it and watches his arc. */
export function crew(g: GoalCtx, prop: PropDef, facing = 0, dz = 0): SpawnDef | null {
  if (prop.id < 0) return null;
  return g.map.spawn({ kind: g.elite ? 'elite' : 'rifleman', x: prop.x, z: prop.z + dz, facing, mans: prop.id });
}

/** A searchlight within a couple of metres of where it is wanted, sweeping about `yaw` (0 = south). */
export function lamp(g: GoalCtx, x: number, z: number, yaw = 0): PropDef | null {
  for (const [dx, dz] of [[0, 0], [1.5, 0], [-1.5, 0], [0, -1.5], [0, 1.5], [2.5, -2], [-2.5, -2]] as const) {
    const prop = g.map.place('searchlight', half(x + dx), half(z + dz), { margin: 0.8, yaw });
    if (prop) return prop;
  }
  return null;
}

/** x of the main road where it crosses this z: the road is the first polyline the generator lays. */
export function roadXAt(g: GoalCtx, z: number): number {
  const points = g.map.roads[0]?.points ?? [];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (!a || !b) continue;
    const [lo, hi] = a[1] < b[1] ? [a, b] : [b, a];
    if (z < lo[1] || z > hi[1]) continue;
    return hi[1] === lo[1] ? lo[0] : lo[0] + ((hi[0] - lo[0]) * (z - lo[1])) / (hi[1] - lo[1]);
  }
  return g.ctx.plan.roadEnd.x;
}

/** The soldiers' path search is bounded (enemies/movement.ts): no leg of a beat may be longer than this. */
const MAX_LEG = 22;

function subdivide(route: readonly Point[]): Point[] {
  const out: Point[] = [];
  for (let i = 0; i + 1 < route.length; i++) {
    const a = route[i];
    const b = route[i + 1];
    if (!a || !b) continue;
    const pieces = Math.max(1, Math.ceil(Math.sqrt((b.x - a.x) * (b.x - a.x) + (b.z - a.z) * (b.z - a.z)) / MAX_LEG));
    for (let p = 0; p < pieces; p++) {
      out.push({ x: a.x + ((b.x - a.x) * p) / pieces, z: a.z + ((b.z - a.z) * p) / pieces });
    }
  }
  const last = route[route.length - 1];
  if (last) out.push(last);
  return out;
}

/** A there-and-back beat along rough points, every node on a free cell; null when one of them has none. */
export function beat(g: GoalCtx, route: readonly Point[], offset = 0): Array<[number, number]> | null {
  const nodes: Array<[number, number]> = [];
  for (const p of subdivide(route)) {
    const spot = g.map.nearestOpen(p.x + offset * 1.4, p.z + offset * 1.1, 4, NO_STAND);
    if (!spot) return null;
    nodes.push(spot);
  }
  for (let i = nodes.length - 2; i >= 1; i--) {
    const back = nodes[i];
    if (back) nodes.push(back);
  }
  return nodes;
}

/** Men who walk a beat in file from the first tick, or - with `wakeTag` - from the timed event that wakes them. */
export function file(g: GoalCtx, kinds: readonly EnemyKind[], route: readonly Point[], wakeTag?: string): number {
  let placed = 0;
  kinds.forEach((kind, m) => {
    const nodes = beat(g, route, m);
    const [first, second] = nodes ?? [];
    if (!nodes || !first) return;
    const facing = second ? atan2(second[0] - first[0], second[1] - first[1]) : 0;
    const dormant: Partial<SpawnDef> = wakeTag === undefined ? {} : { dormantUntil: 'timedEvent', wakeTag };
    g.map.spawn({ kind, x: first[0], z: first[1], facing, patrol: nodes, ...dormant });
    for (const [x, z] of nodes) g.map.mask.circle(x, z, 1.2, FLAG.keep);
    placed++;
  });
  if (placed < kinds.length) g.map.problems.push(`${g.ctx.spec.id}: nowhere for ${wakeTag ?? 'the patrol'} to walk`);
  return placed;
}

/** Riflemen, with an elite among them from threat 6 (02 §9.1) - all elites where the garrison is. */
export function squad(g: GoalCtx, count: number): EnemyKind[] {
  const kindOf = (i: number): EnemyKind => (g.elite || (g.threat >= 6 && i === 0) ? 'elite' : 'rifleman');
  return Array.from({ length: count }, (_, i) => kindOf(i));
}
